// Meta Manager is the source of truth for templates. This file is only the variable
// vocabulary — no per-template entries; sendability helpers live in cpool-backend.
//
// Naming convention for vocabulary keys (must match Meta variable names exactly):
//   - snake_case
//   - prefixed `agent_*` / `applicant_*` / `partner_*` for agent/applicant/partner-sourced values
//   - free semantic names for `{ source: 'custom' }` (adviser-typed at send time)
//   - free semantic names for `{ source: 'computed' }` (server-derived values like crypto/links)

import type { ValueOf } from './util/typeUtils';

export const WHATSAPP_TEMPLATE_VARIABLE_SOURCES = {
    AGENT: 'agent',
    APPLICANT: 'applicant',
    COMPUTED: 'computed',
    CUSTOM: 'custom',
    PARTNER: 'partner',
} as const;

export type WhatsAppTemplateVariableSourceKind = ValueOf<typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES>;

/** Keys backing `{ source: 'computed' }` — server-derived (crypto/links), never previewable on the frontend. */
export const COMPUTED_WHATSAPP_VARIABLE_KEYS = {
    DOKUMENT_UPLOAD_LINK: 'dokument_upload_link',
    TRUSTPILOT_LINK: 'trustpilot_link',
} as const;

export type ComputedWhatsAppVariableKey = ValueOf<typeof COMPUTED_WHATSAPP_VARIABLE_KEYS>;

export type TemplateVariableSource =
    | {
          source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT;
          field: 'firstName' | 'lastName' | 'email' | 'phone' | 'title' | 'salutation';
      }
    | { source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT; field: 'firstName' | 'lastName' | 'salutation' }
    // The chat's linked partner (partner chats only).
    | { source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER; field: 'firstName' | 'lastName' | 'email' }
    // Resolved server-side at send time (crypto/links) — no per-template config, name IS the key.
    | { source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED; key: ComputedWhatsAppVariableKey }
    // Caller-supplied at send time; Meta approves the slot shape, not the value source.
    | { source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM };

/** Derived parameter shape after catalog resolution — not Meta's `parameter_format`. */
export const WHATSAPP_TEMPLATE_PARAMETER_KINDS = {
    NAMED: 'named',
    NONE: 'none',
    POSITIONAL: 'positional',
} as const;

export type WhatsAppTemplateParameterKind = ValueOf<typeof WHATSAPP_TEMPLATE_PARAMETER_KINDS>;

export type ApprovedWhatsAppTemplateParameters =
    | { kind: typeof WHATSAPP_TEMPLATE_PARAMETER_KINDS.NONE }
    | { kind: typeof WHATSAPP_TEMPLATE_PARAMETER_KINDS.POSITIONAL; count: number }
    | { kind: typeof WHATSAPP_TEMPLATE_PARAMETER_KINDS.NAMED; variables: Record<string, TemplateVariableSource> };

export type ApprovedWhatsAppTemplate = {
    languages: readonly string[];
    parameters: ApprovedWhatsAppTemplateParameters;
    // UX modal only (picker + preview).
    displayName: string;
    folder: string;
    body: Record<string, string>;
};

// Only TEXT has a resolvable placeholder; media formats are blocked as UNSUPPORTED_HEADER_FORMAT.
export const WHATSAPP_TEMPLATE_HEADER_FORMATS = {
    DOCUMENT: 'DOCUMENT',
    IMAGE: 'IMAGE',
    LOCATION: 'LOCATION',
    TEXT: 'TEXT',
    VIDEO: 'VIDEO',
} as const;

export type WhatsAppTemplateHeaderFormat = ValueOf<typeof WHATSAPP_TEMPLATE_HEADER_FORMATS>;

/** Meta `parameter_format` normalized for catalog storage / derivation. */
export const WHATSAPP_TEMPLATE_PARAMETER_FORMATS = {
    NAMED: 'NAMED',
    NONE: 'NONE',
    POSITIONAL: 'POSITIONAL',
} as const;

export type WhatsAppTemplateParameterFormat = ValueOf<typeof WHATSAPP_TEMPLATE_PARAMETER_FORMATS>;

/** Meta rejects WhatsApp template parameter values containing newlines, tabs, or 4+ consecutive spaces. */
export const WHATSAPP_TEMPLATE_PARAMETER_INVALID_CHARS_REGEX = /[\n\r\t]| {4,}/;

export const WHATSAPP_TEMPLATE_PARAMETER_MAX_LENGTH = 1024;

/**
 * Global variable vocabulary. Keys must match Meta named-variable placeholders exactly.
 * Each key also needs `WHATSAPP_CHAT.TEMPLATE_MODAL.VARIABLES.<key>` in frontend de + en
 * (guarded by `knownWhatsAppVariableTranslations.test.ts`).
 */
export const KNOWN_WHATSAPP_VARIABLES_BY_NAME = {
    agent_email: { field: 'email', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    agent_first_name: { field: 'firstName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    agent_last_name: { field: 'lastName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    agent_phone: { field: 'phone', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    agent_salutation: { field: 'salutation', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    agent_title: { field: 'title', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.AGENT },
    applicant_first_name: { field: 'firstName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT },
    applicant_last_name: { field: 'lastName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT },
    applicant_salutation: { field: 'salutation', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.APPLICANT },
    dokument_upload_link: {
        key: COMPUTED_WHATSAPP_VARIABLE_KEYS.DOKUMENT_UPLOAD_LINK,
        source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED,
    },
    // ETA freitext analogue.
    freitext: { source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM },
    partner_email: { field: 'email', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER },
    partner_first_name: { field: 'firstName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER },
    partner_last_name: { field: 'lastName', source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.PARTNER },
    trustpilot_link: {
        key: COMPUTED_WHATSAPP_VARIABLE_KEYS.TRUSTPILOT_LINK,
        source: WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED,
    },
} satisfies Record<string, TemplateVariableSource>;

export type KnownWhatsAppVariableName = keyof typeof KNOWN_WHATSAPP_VARIABLES_BY_NAME;

/** Agent/applicant/partner variable names (excl. custom/computed — not previewable on FE). */
export type WhatsAppVariableName = {
    [K in KnownWhatsAppVariableName]: (typeof KNOWN_WHATSAPP_VARIABLES_BY_NAME)[K] extends {
        source: typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.COMPUTED | typeof WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM;
    }
        ? never
        : K;
}[KnownWhatsAppVariableName];

export const KNOWN_WHATSAPP_VARIABLES: typeof KNOWN_WHATSAPP_VARIABLES_BY_NAME &
    Record<string, TemplateVariableSource> = KNOWN_WHATSAPP_VARIABLES_BY_NAME;

export const WHATSAPP_TEMPLATE_BLOCK_REASONS = {
    UNKNOWN_VARIABLES: 'UNKNOWN_VARIABLES',
    UNSUPPORTED_BUTTON_TYPE: 'UNSUPPORTED_BUTTON_TYPE',
    UNSUPPORTED_BUTTON_UNSYNCED: 'UNSUPPORTED_BUTTON_UNSYNCED',
    UNSUPPORTED_BUTTON_URL_PREFIX: 'UNSUPPORTED_BUTTON_URL_PREFIX',
    UNSUPPORTED_HEADER_FORMAT: 'UNSUPPORTED_HEADER_FORMAT',
    UNSUPPORTED_HEADER_UNSYNCED: 'UNSUPPORTED_HEADER_UNSYNCED',
    UNSUPPORTED_HEADER_VARS_UNMATCHED: 'UNSUPPORTED_HEADER_VARS_UNMATCHED',
} as const;

export type WhatsAppTemplateBlockReason = ValueOf<typeof WHATSAPP_TEMPLATE_BLOCK_REASONS>;

// UNKNOWN = unmodeled kind; kept for index, blocks the template.
export const WHATSAPP_TEMPLATE_BUTTON_TYPES = {
    PHONE_NUMBER: 'PHONE_NUMBER',
    QUICK_REPLY: 'QUICK_REPLY',
    UNKNOWN: 'UNKNOWN',
    URL: 'URL',
} as const;

export type WhatsAppTemplateButtonType = ValueOf<typeof WHATSAPP_TEMPLATE_BUTTON_TYPES>;

// Literal Meta-approved prefixes (not env URLs) → computed suffix key; matched exactly, fail-closed.
export const KNOWN_WHATSAPP_URL_BUTTON_PREFIXES: Record<string, ComputedWhatsAppVariableKey> = {
    'https://de.trustpilot.com/evaluate-bgl/kredit.de?p=': COMPUTED_WHATSAPP_VARIABLE_KEYS.TRUSTPILOT_LINK,
    'https://dokumente.dev.kredit.de/?pl=': COMPUTED_WHATSAPP_VARIABLE_KEYS.DOKUMENT_UPLOAD_LINK,
    'https://dokumente.kredit.de/?pl=': COMPUTED_WHATSAPP_VARIABLE_KEYS.DOKUMENT_UPLOAD_LINK,
    'https://dokumente.staging.kredit.de/?pl=': COMPUTED_WHATSAPP_VARIABLE_KEYS.DOKUMENT_UPLOAD_LINK,
};

// Optional Aktionstext (customer bubble) and/or auto-reply (adviser message back).
export type WhatsAppTemplateButtonAction = {
    actionText?: string;
    autoReply?: string;
};

// templateName → Meta BUTTONS index → action. Reorder/rename in Meta invalidates entries — re-check the catalog row.
// Only templates from the Confluence catalog with fully-resolved button copy are mapped; BLOCKER/unclear rows and
// duplicate-named rows (nachfassen_tag_1, nachfassen_tag_2, genehmigung) are intentionally left out.
export const WHATSAPP_TEMPLATE_BUTTON_ACTIONS: Record<string, Record<string, WhatsAppTemplateButtonAction>> = {
    abbruch_nicht_erreicht: {
        '0': {
            actionText: 'Ich möchte den Vorgang bitte stornieren.',
            autoReply: 'Vielen Dank für die Rückmeldung. Bitte nennen Sie mir nur noch den Grund für die Stornierung?',
        },
        '1': { actionText: 'Bitte stornieren Sie den Vorgang nicht, ich habe noch Interesse!' },
    },
    angaben_vervollstaendigen: {
        '0': { actionText: 'Ja gerne.' },
        '1': {
            actionText: 'Ich möchte den Vorgang bitte stornieren.',
            autoReply: 'Vielen Dank für die Rückmeldung. Bitte nennen Sie mir nur noch den Grund für die Stornierung?',
        },
    },
    nachfassen_letzte_erinnerung: {
        '0': {
            actionText: 'Ich möchte den Vorgang bitte stornieren.',
            autoReply: 'Vielen Dank für die Rückmeldung. Bitte nennen Sie mir nur noch den Grund für die Stornierung?',
        },
        '1': {
            actionText: 'Bitte stornieren Sie den Vorgang nicht, ich habe noch Interesse!',
            autoReply: 'Vielen Dank für die Rückmeldung, zu wann schaffen Sie es mir die Unterlagen zu schicken?',
        },
    },
    willkommen: {
        '0': { actionText: 'Ich möchte bitte angerufen werden.' },
        '1': { actionText: 'Ich möchte den Kontakt über WhatsApp!' },
        '2': { actionText: 'Kontaktieren Sie mich bitte via E-Mail' },
    },
};

/** Curated UX overlay — not from Meta. Overlay miss → titleCase(name) / folder `'Allgemein'`. */
// Only Confluence rows marked "KANN ANGELEGT WERDEN" (no open BLOCKER) are mapped.
export const WHATSAPP_TEMPLATE_DISPLAY_OVERLAY: Record<string, { displayName: string; folder: string }> = {
    abbruch_nicht_erreicht: { displayName: 'Abbruch: nicht erreicht', folder: 'Allgemein' },
    abbruch_nicht_verwertbar_mitantragsteller: {
        displayName: 'Abbruch: nicht verwertbar - Mitantragsteller',
        folder: 'PK',
    },
    angebot_laeuft_ab: { displayName: 'Angebot läuft ab', folder: 'PK' },
    angebot_versendet: { displayName: 'Angebot versendet', folder: 'Allgemein' },
    angebot_versendet_erinnerung: { displayName: 'Angebot versendet Erinnerung', folder: 'PK' },
    willkommen: { displayName: 'Willkommen', folder: 'Allgemein' },
};

/** Singleton WABA stream for this deployment (template catalog + future WABA-level events). */
export const WABA_AGGREGATE_ID = 'a1b2c3d4-e5f6-4789-a012-3456789abcde';

export const WHATSAPP_TEMPLATE_DEFAULT_FOLDER = 'Allgemein';

export const WHATSAPP_QUICK_REPLY_TEMPLATE_PREFIX = 'schnelle_antwort_';
/** Meta Manager sample templates on the live WABA — ingested never, cannot be deleted in Meta. */
export const WHATSAPP_SAMPLE_TEMPLATE_NAME_PREFIX = 'sample_';
